import React, { useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { RuleTemplate } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

import GoogleAdsRuleTemplateForm from './GoogleAdsRuleTemplateForm';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 0.5,
};

const getColumns = ({
  onEdit,
  onDelete,
}: {
  onEdit: (row: RuleTemplate) => void;
  onDelete: (row: RuleTemplate) => void;
}): Array<GridColDef> => [
  {
    field: 'name',
    headerName: 'Name',
    ...sharedOpts,
    flex: 1,
  },
  {
    field: 'description',
    headerName: 'Description',
    ...sharedOpts,
    flex: 1.5,
    renderCell: (params) => <>{params.row.description || '-'}</>,
  },
  {
    field: 'primary_metrics',
    headerName: 'Primary Metrics',
    ...sharedOpts,
    flex: 1,
    renderCell: (params) => <>{params.row.primary_metrics?.join(', ')}</>,
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    ...sharedOpts,
    renderCell: (params) => <>{dateFormatter(params.row.created_at)}</>,
  },
  {
    field: 'updated_at',
    headerName: 'Updated At',
    ...sharedOpts,
    renderCell: (params) => <>{dateFormatter(params.row.updated_at)}</>,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    ...sharedOpts,
    flex: 0.8,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button variant="outlined" size="small" onClick={() => onEdit(params.row)}>
          Edit
        </Button>
        <Button variant="outlined" size="small" color="error" onClick={() => onDelete(params.row)}>
          Delete
        </Button>
      </Stack>
    ),
  },
];

type Props = {
  data: Array<RuleTemplate>;
  isLoading: boolean;
  totalCount: number;
  pageNum: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  handleOpen: () => void;
  handleEditTemplate: (template: RuleTemplate) => void;
  handleDeleteTemplate: (template: RuleTemplate) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  editableTemplate: RuleTemplate | null;
  fetchData: () => void;
};

export default function GoogleAdsRuleTemplatesTab({
  data,
  isLoading,
  totalCount,
  pageNum,
  pageSize,
  onPageChange,
  handleOpen,
  handleEditTemplate,
  handleDeleteTemplate,
  open,
  setOpen,
  editableTemplate,
  fetchData,
}: Props) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<RuleTemplate | undefined>(undefined);

  const handleDeleteClick = (template: RuleTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (templateToDelete) {
      handleDeleteTemplate(templateToDelete);
      setDeleteDialogOpen(false);
      setTemplateToDelete(undefined);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(undefined);
  };

  return (
    <>
      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item xs={12}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={handleOpen}>
              New Template
            </Button>
          </Stack>
        </Grid>
      </Grid>

      <GoogleAdsRuleTemplateForm
        open={open}
        setOpen={setOpen}
        fetchData={fetchData}
        editableTemplate={editableTemplate}
      />

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the rule template: {templateToDelete?.name}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} variant="text">
            Cancel
          </Button>
          <Button onClick={confirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={data}
          columns={getColumns({ onEdit: handleEditTemplate, onDelete: handleDeleteClick })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          getRowId={(row) => row.id}
        />
      </Box>
    </>
  );
}
